// packages/web-ui/src/AppShell/HealthStatusBanner.tsx
//
// Top banner — Audi-inspired chrome:
//   • Pure black background, white foreground (high optical contrast,
//     mirrors audi.com's primary nav surface).
//   • Wordmark left-aligned with an Audi-red accent stripe.
//   • Capability pills + optional VS-Code-host pill right-aligned.
//   • 44 px (`--space-h-banner`) gives the wordmark enough breathing
//     room and matches the type-rhythm.

import type { JSX } from 'react';
import { PANEL_DEFAULT_WIDTHS, Z_INDEX } from './responsive-tokens.js';
import { CapabilityPillRow } from '../components/capability-pills/index.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { UserMenu } from '../components/auth/UserMenu.js';

export interface HealthStatusBannerProps {
  hostedInVsCode?: boolean;
}

export function HealthStatusBanner({ hostedInVsCode = false }: HealthStatusBannerProps): JSX.Element {
  const { capabilities, loading, error } = useCapabilities();
  return (
    <header
      role="banner"
      aria-label="TESSA Lean status"
      data-testid="health-status-banner"
      className="flex items-center justify-between gap-3 px-4 text-xs text-white"
      style={{
        height: PANEL_DEFAULT_WIDTHS.banner,
        zIndex: Z_INDEX.healthStatusBanner,
        backgroundColor: '#000000',
        borderBottom: '1px solid #000000',
      }}
    >
      {/* Left wordmark — allowed to shrink (truncates the "Lean" tag) so the
          status cluster on the right always stays visible. */}
      <div className="flex min-w-0 items-center gap-3 overflow-hidden">
        <span
          aria-hidden="true"
          className="bg-audi-red inline-block h-4 w-[3px] flex-shrink-0"
        />
        <span className="flex-shrink-0 text-[13px] font-extrabold uppercase tracking-[0.22em]">
          TESSA
        </span>
        <span
          aria-hidden="true"
          className="inline-block h-3 w-px flex-shrink-0"
          style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}
        />
        <span className="truncate text-[11px] font-medium uppercase tracking-[0.14em] text-white/70">
          Lean
        </span>
      </div>
      {/* Right status cluster — capability pills + user menu. `flex-shrink-0`
          keeps it intact at narrow viewports so the admin menu never gets
          pushed behind the last pill. */}
      <div className="flex flex-shrink-0 items-center gap-3">
        <CapabilityPillRow
          capabilities={capabilities}
          loading={loading}
          error={error}
          testId="health-status-banner-pills"
        />
        {hostedInVsCode ? (
          <span
            data-testid="hosted-in-vs-code-pill"
            className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide"
            style={{
              border: '1px solid rgba(255,255,255,0.25)',
              color: '#FFFFFF',
            }}
          >
            Hosted in VS Code
          </span>
        ) : null}
        <UserMenu />
      </div>
    </header>
  );
}
